import { ChevronDown, SearchX, ArrowDownUp, ArrowUp, ArrowDown, Inbox, ClipboardX, FileKey } from 'lucide-react';
import { useSuspenseQuery } from '@apollo/client/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import { Fragment, useMemo, useRef, useState, useTransition } from 'react';
import dayjs from 'dayjs';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSaasEscrow } from 'src/context/saas-escrow';
import { DepositStatusEnum, ContentTypeEnum, CredentialTypeEnum } from 'src/graphql/schema';
import { SaasCredentialsDocument } from 'src/graphql/actions/saasCredentials/saasCredentials.query';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import { useDepositsSuggestChanges } from 'src/context/suggest-changes';
import { EmptyBlock } from '@/components/ui/empty-block';
import Paginator from '@/components/ui/paginator';
import { credentialTypeOptionsMap } from 'src/containers/Credentials';

import type { ISaasCredentialsQueryVariables } from 'src/graphql/actions/saasCredentials/saasCredentials.query';

import AssetActions from './AssetActions';
import CredentialsFilters from './CredentialsFilters';

type Props = {
  title: string;
  description: string;
  totalAssets: number;
};

type ICredentialRow = {
  id: string;
  credential: {
    name: string;
    providerName: string;
  };
  type: CredentialTypeEnum;
  date: string;
  depositStatus: DepositStatusEnum;
  action: {
    id: string;
    canRemove: boolean;
    isLast: boolean;
  };
};

const columnHelper = createColumnHelper<ICredentialRow>();
const columns = [
  columnHelper.accessor('credential', {
    header: (context) => `Assets (${context.table.getRowModel().rows.length})`,
    cell: (props) => {
      const { name, providerName } = props.getValue();

      return (
        <div className="flex items-center gap-2">
          <FileKey className="size-5 shrink-0" />
          <div className="grow">
            <div className="truncate max-w-[126px]" title={name}>
              {name}
            </div>
            <div className="text-xs text-muted-foreground">{providerName}</div>
          </div>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.credential.name;
      const valueB = rowB.original.credential.name;

      return valueA.localeCompare(valueB);
    },
  }),
  columnHelper.accessor('type', {
    header: 'Credential type',
    cell: (props) => credentialTypeOptionsMap[props.getValue()],
    enableSorting: false,
  }),
  columnHelper.accessor('date', {
    header: 'Deposit timestamp',
    cell: (props) => {
      const value = props.getValue();

      if (!value) return null;

      const date = dayjs(value).format('MMM DD, YYYY');
      const time = dayjs(value).format('HH:mm A');

      return (
        <Fragment>
          <div className="text-sm font-medium">{date}</div>
          <div className="text-xs text-muted-foreground">{time}</div>
        </Fragment>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.date ? dayjs(rowA.original.date).unix() : 0;
      const valueB = rowB.original.date ? dayjs(rowB.original.date).unix() : 0;

      return valueA - valueB;
    },
  }),
  columnHelper.accessor('depositStatus', {
    header: 'Deposit status',
    cell: () => <Badge>Taken</Badge>,
    sortingFn: 'text',
  }),

  columnHelper.accessor('action', {
    header: '',
    cell: (props) => {
      const { id, canRemove, isLast } = props.getValue();

      return (
        <AssetActions
          id={id}
          depositSource={props.row.original.credential.name}
          canRemove={canRemove}
          isLast={isLast}
          type="credentials"
        />
      );
    },
    enableSorting: false,
    meta: {
      headerClassName: 'w-16',
      cellClassName: 'w-16',
    },
  }),
];

const SuggestChangesAssetsBlock = ({ title, description, totalAssets }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isPending, startTransition] = useTransition();
  const {
    saasEscrow: { id: saasEscrowId, requiredContentTypes },
  } = useSaasEscrow();
  const initialFilters = {
    saasEscrowId,
    perPage: 10,
    page: 1,
  };

  const [filters, setFilters] = useState<ISaasCredentialsQueryVariables>(initialFilters);
  const {
    data: { saasCredentials },
  } = useSuspenseQuery(SaasCredentialsDocument, {
    variables: filters,
  });
  const { state, allDeletedAssets, allDeletedCredentials } = useDepositsSuggestChanges();
  const totalContentAssetsRef = useRef(saasCredentials.nodesCount);
  const tableData = useMemo(() => {
    const totalDeletedAssets = [...allDeletedAssets, ...allDeletedCredentials];

    return saasCredentials.nodes.map((node) => ({
      id: node.id,
      credential: {
        name: node.name,
        providerName: node.providerName,
      },
      type: node.credentialType,
      date: node.createdAt,
      depositStatus: DepositStatusEnum.Taken,
      action: {
        id: node.id,
        canRemove: !totalDeletedAssets.some((asset) => asset.id === node.id),
        isLast: totalAssets - 1 === totalDeletedAssets.length,
      },
    }));
  }, [saasCredentials.nodes, totalAssets, allDeletedAssets, allDeletedCredentials]);
  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  if (!totalContentAssetsRef.current) return null;

  if (!requiredContentTypes.includes(ContentTypeEnum.Credentials))
    return (
      <Card className="bg-[#F1F5F9]">
        <CardHeader>
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <EmptyBlock
            title={
              <Fragment>
                This asset type has been marked as <strong>Not required</strong> by the depositor.
              </Fragment>
            }
            icon={ClipboardX}
            withCard={false}
            classes={{
              title: 'text-sm',
              content: 'bg-[#F1F5F9]',
              icon: 'bg-[#F1F5F9]',
            }}
          />
        </CardContent>
      </Card>
    );

  if (!totalContentAssetsRef.current)
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <EmptyBlock
            title="No assets added"
            icon={Inbox}
            withCard={false}
            classes={{
              title: 'text-sm',
            }}
          />
        </CardContent>
      </Card>
    );

  const setCredentialTypeFilter = (credentialType?: CredentialTypeEnum) =>
    startTransition(() => setFilters((prevFilters) => ({ ...prevFilters, credentialType })));

  const setQueryFilter = (query?: string) => setFilters((prevFilters) => ({ ...prevFilters, query, page: 1 }));

  const onPageChange = (page: number) => startTransition(() => setFilters((prevFilters) => ({ ...prevFilters, page })));

  const infoItems = [
    {
      label: 'Total number of assets',
      value: totalContentAssetsRef.current,
    },
    {
      label: 'Removal requests',
      value: state['credentials'].length,
      className: cn(state['credentials'].length ? 'bg-accent' : 'bg-transparent'),
    },
  ];

  const setInitialFilters = () => setFilters(initialFilters);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Card>
        <CardHeader className="flex-row justify-between items-center gap-2 space-y-0 [&:has(+[data-state=open])]:pb-0">
          <div className="text-base font-semibold">{title}</div>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-xs">
              {isOpen ? 'Hide' : 'Show'} assets
              <ChevronDown className={cn('transition-transform rotate-0', isOpen && 'rotate-180')} />
            </Button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent className="pb-0">
          <CardContent className="space-y-6">
            <CardDescription>{description}</CardDescription>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {infoItems.map(({ label, value, className }) => (
                <div
                  key={label}
                  className={cn('bg-background-secondary border border-border rounded-lg p-5 space-y-1', className)}
                >
                  <div className="text-lg font-bold text-foreground">{value}</div>
                  <div className="text-xs text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <CredentialsFilters
                query={filters.query ?? undefined}
                credentialType={filters.credentialType ?? undefined}
                setCredentialTypeFilter={setCredentialTypeFilter}
                setQueryFilter={setQueryFilter}
                setInitialFilters={setInitialFilters}
              />
              <div className="border border-border rounded-md">
                <Table isLoading={isPending}>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(
                              'bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                              header.column.columnDef.meta?.headerClassName,
                            )}
                          >
                            {header.column.getCanSort() ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={header.column.getToggleSortingHandler()}
                                className="-ml-3 text-muted-foreground! text-xs! font-bold"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: <ArrowUp />,
                                  desc: <ArrowDown />,
                                }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                              </Button>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                !row.original.action.canRemove && cell.column.id !== 'action' && 'opacity-30',
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                          <EmptyBlock
                            title="No results"
                            icon={SearchX}
                            withCard={false}
                            classes={{
                              title: 'text-sm',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <Paginator
                currentPage={filters.page || 1}
                totalPages={saasCredentials.pagesCount}
                onPageChange={onPageChange}
                showPreviousNext
                hideOnSinglePage
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default withSuspense(SuggestChangesAssetsBlock);
